import axios, { AxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/react";
import { backendUrl } from "../utils/Environment";

const axiosInstance = axios.create({
  baseURL: backendUrl,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

axiosInstance.interceptors.request.use(async (config) => {
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const success = await refreshAuthToken();
      if (success) {
        return axiosInstance({ ...originalRequest, _retry: false });
      }
    }

    if (error.response) {
      const status = error.response.status;
      const url = error.config?.url || "unknown";
      const method = error.config?.method || "unknown";

      if (status >= 500) {
        Sentry.captureException(error, {
          tags: {
            errorType: "api_error",
            statusCode: status,
            endpoint: url,
            method: method,
          },
          contexts: {
            api: {
              url: url,
              method: method,
              status: status,
              response: error.response?.data,
            },
          },
        });
      } else if (status >= 400 && status !== 401 && status !== 403) {
        Sentry.captureMessage(`API Error: ${method.toUpperCase()} ${url} - ${status}`, {
          level: "warning",
          tags: {
            errorType: "api_client_error",
            statusCode: status,
            endpoint: url,
            method: method,
          },
        });
      }
    } else if (error.request) {
      Sentry.captureException(error, {
        tags: {
          errorType: "network_error",
        },
        contexts: {
          network: {
            url: error.config?.url,
            method: error.config?.method,
          },
        },
      });
    }

    return Promise.reject(error);
  }
);

const get = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await axiosInstance.get<T>(url, config);
  return response.data;
};

const post = async <T, U = any>(
  url: string,
  data: U,
  config?: AxiosRequestConfig
): Promise<T> => {
  const response = await axiosInstance.post<T>(url, data, config);
  return response.data;
};

const put = async <T, U = any>(
  url: string,
  data: U,
  config?: AxiosRequestConfig
): Promise<T> => {
  const response = await axiosInstance.put<T>(url, data, config);
  return response.data;
};

const patch = async <T, U = any>(
  url: string,
  data: U,
  config?: AxiosRequestConfig
): Promise<T> => {
  const response = await axiosInstance.patch<T>(url, data, config);
  return response.data;
};

const del = async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const response = await axiosInstance.delete<T>(url, config);
  return response.data;
};

export const httpService = {
  get,
  post,
  put,
  patch,
  delete: del,
};

async function refreshAuthToken(): Promise<boolean> {
  try {
    console.log("Attempting to refresh token...");
    await axiosInstance.post("/tokens/refresh");
    console.log("Token refreshed successfully.");
    return true;
  } catch (error: any) {
    console.error("Failed to refresh token:", error);

    if (axios.isAxiosError(error)) {
      const responseStatus = error.response?.status;
      const errorMessage = error.response?.data?.message;

      if (responseStatus === 403 && errorMessage === "Refresh token expired") {
        console.warn("Refresh token has expired. User must log in again.");

        alert("Your session has expired. Please log in again.");
        localStorage.clear();
        sessionStorage.clear();

        window.location.href = "/auth";
      }
    }

    return false;
  }
}
